import type { VoidComponent } from "solid-js";
import { createSignal, Match, Show, Switch, untrack } from "solid-js";
import type { Subform } from "../../../state/formVersion";
import EditorContent from "../editor/EditorContent";
import { FormServicesProvider } from "../../../services/formServices";
import FormServicesFactory from "../../../services/FormServicesFactory";
import RulesContent from "../rules/RulesContent";
import SubformEditorHeader from "./SubformEditorHeader";
import type { SubformEditorTab } from "./types";
import FormEditorDragDropProvider from "../FormEditorDragDropProvider";
import SubformEditorEmailContent from "./SubformEditorEmailContent";
import type { SidePanelSelection } from "../../../components/panels/FormEditorSidePanel";
import { FieldEditorSidePanel } from "../../../components/panels/FormEditorSidePanel";
import { FormEditorSidePanelContext } from "../FormEditorContent";
import { narrow } from "../../../utils/solidUtils";
import { SideNavContent } from "../sideNav/SideNavContent";

type SubformEditorProps = {
  subform: Subform;
  tab: SubformEditorTab;
  onTabChange: (tab: SubformEditorTab) => void;
};

const SubformEditor: VoidComponent<SubformEditorProps> = (props) => {
  let contentContainerRef!: HTMLDivElement;
  const [selectedPanel, setSelectedPanel] = createSignal<SidePanelSelection | null>(null);

  return (
    // We can safely `untrack` since the subform uid value never changes after this point. When opening close this
    // subform and opening a new one, this whole component (and so also the context) is destroyed and re-created.
    <FormEditorSidePanelContext.Provider value={{ selectedPanel, setSelectedPanel }}>
      <FormServicesProvider value={FormServicesFactory.forSubform(untrack(() => props.subform.uid))}>
        <FormEditorDragDropProvider>
          <div class="flex h-full min-w-desktop flex-col">
            <SubformEditorHeader subform={props.subform} activeItem={props.tab} onMenuChange={props.onTabChange} />
            <div class="flex flex-1 overflow-hidden">
              <SideNavContent activeItem={props.tab} />
              <main
                class="flex flex-1 flex-col items-center overflow-y-auto bg-surface-extra-subtle px-8 py-15 -outline-offset-1 focus-visible:outline-default"
                ref={contentContainerRef}
              >
                <div class="flex w-full max-w-182 flex-col">
                  <Switch>
                    <Match when={props.tab === "fields"}>
                      <EditorContent contentContainer={contentContainerRef} />
                    </Match>
                    <Match when={props.tab === "rules"}>
                      <RulesContent />
                    </Match>
                    <Match when={props.tab === "mail"}>
                      <SubformEditorEmailContent subform={props.subform} />
                    </Match>
                  </Switch>
                </div>
              </main>
              <Show when={props.tab === "fields"}>
                <Switch>
                  <Match when={narrow(selectedPanel(), (panel) => panel.type === "field")}>
                    {(panel) => <FieldEditorSidePanel {...panel()} />}
                  </Match>
                </Switch>
              </Show>
            </div>
          </div>
        </FormEditorDragDropProvider>
      </FormServicesProvider>
    </FormEditorSidePanelContext.Provider>
  );
};

export default SubformEditor;
